import React, { useState, useEffect, useRef } from 'react';
import { Sun, Moon, Search, Bell, User, ExternalLink, Play, Book, Code2, ChevronRight, Menu, X } from 'lucide-react';

const LearningPage = () => {
  const [darkMode, setDarkMode] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const cubeRef = useRef(null);
  const coneRef = useRef(null);

  useEffect(() => {
    // 3D Animation for quantum elements
    let animationId;
    const animate = () => {
      const time = Date.now() * 0.001;
      
      if (cubeRef.current) {
        cubeRef.current.style.transform = `rotateX(${time * 20}deg) rotateY(${time * 30}deg) rotateZ(${time * 10}deg)`;
      }
      
      if (coneRef.current) {
        coneRef.current.style.transform = `rotateX(${time * 15}deg) rotateY(${time * 25}deg)`;
      }
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    return () => cancelAnimationFrame(animationId);
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const CourseCard = ({ category, title, description, isNew = false, buttonText = "Course" }) => (
    <div className={`${
      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    } border rounded-xl p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
      {category && (
        <div className={`text-xs mb-3 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {category}
        </div>
      )}
      <h3 className={`text-xl font-semibold mb-4 leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {title}
      </h3>
      <p className={`text-sm mb-6 leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {description}
      </p>
      <div className="flex items-center gap-3">
        <button className="px-6 py-2.5 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200">
          {buttonText}
        </button>
        {isNew && (
          <span className="px-3 py-1 text-xs bg-purple-600 text-white rounded-full font-semibold">
            New
          </span>
        )}
      </div>
    </div>
  );

  const ResourceCard = ({ icon: Icon, title, description, link }) => (
    <div className="text-center group cursor-pointer">
      <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center transition-all duration-300 ${
        darkMode ? 'bg-gray-800 group-hover:bg-gray-700' : 'bg-gray-100 group-hover:bg-gray-200'
      } group-hover:scale-110`}>
        <Icon className="w-10 h-10 text-blue-500" />
      </div>
      <h3 className={`text-xl font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {title}
      </h3>
      <p className={`text-sm mb-4 leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        {description}
      </p>
      {link && (
        <button className="text-blue-500 hover:text-blue-600 text-sm font-medium inline-flex items-center gap-2 group-hover:gap-3 transition-all">
          Explore {title.toLowerCase()}
          <ExternalLink className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  return (
    <div className={`min-h-screen transition-all duration-500 ${
      darkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      {/* Header */}
      <header className={`sticky top-20 z-50 ${
        darkMode ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 border-gray-200'
      } border-b backdrop-blur-sm`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-20 px-4 sm:px-6 lg:px-8">
            {/* Left side */}
            <div className="flex items-center space-x-8">
              <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                GLOCYBS Platform
              </h1>
              <nav className="hidden lg:flex space-x-8">
                <button className="text-blue-500 border-b-2 border-blue-500 pb-6 text-base font-semibold">
                  Learning
                </button>
                <button className={`${
                  darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                } text-base font-semibold pb-6 transition-colors`}>
                  Composer
                </button>
                <button className={`${
                  darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                } text-base font-semibold pb-6 transition-colors`}>
                  Documentation
                </button>
              </nav>
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {/* Search - Hidden on mobile */}
              <div className="hidden md:block relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`} />
                <input 
                  type="text" 
                  placeholder="Search courses..." 
                  className={`pl-12 pr-4 py-3 text-sm rounded-xl border transition-all w-64 ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>

              {/* Icons */}
              <button
                onClick={toggleDarkMode}
                className={`p-3 rounded-xl transition-all ${
                  darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {darkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
              </button>

              <button className={`p-3 rounded-xl transition-all ${
                darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}>
                <Bell className="w-6 h-6" />
              </button>

              <button className={`p-3 rounded-xl transition-all ${
                darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}>
                <User className="w-6 h-6" />
              </button>

              {/* Mobile menu button */}
              <button
                onClick={toggleMobileMenu}
                className={`lg:hidden p-3 rounded-xl transition-all ${
                  darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className={`lg:hidden border-t ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} px-4 py-6`}>
              <nav className="space-y-4">
                <button className="block w-full text-left text-blue-500 text-lg font-semibold">Learning</button>
                <button className={`block w-full text-left text-lg font-semibold ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>Composer</button>
                <button className={`block w-full text-left text-lg font-semibold ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>Documentation</button>
              </nav>
              {/* Mobile search */}
              <div className="mt-6 relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`} />
                <input 
                  type="text" 
                  placeholder="Search courses..." 
                  className={`w-full pl-12 pr-4 py-3 text-sm rounded-xl border transition-all ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Feedback Banner */}
      <div className={`${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'} border-b`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center text-sm">
            <div className="w-5 h-5 mr-4 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs text-white font-bold">!</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`${darkMode ? 'text-blue-200' : 'text-blue-800'} font-medium`}>
                Help us improve GLOCYBS Platform!
              </span>
              <button className="text-blue-500 underline hover:no-underline font-medium">
                Share your thoughts in our annual feedback survey
              </button>
              <ChevronRight className="w-4 h-4 text-blue-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section with 3D Design */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <h1 className={`text-5xl lg:text-7xl font-light leading-tight ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Quantum
              <br />
              <span className="text-blue-500">learning</span>
            </h1>
            <p className={`text-xl lg:text-2xl leading-relaxed ${
              darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Kickstart your quantum learning journey with a selection of courses designed to help you learn the basics or explore more focused topics.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors text-lg">
                Start Learning
              </button>
              <button className={`px-8 py-4 border-2 font-semibold rounded-xl transition-colors text-lg ${
                darkMode 
                  ? 'border-gray-600 text-gray-300 hover:border-gray-500 hover:text-white' 
                  : 'border-gray-300 text-gray-700 hover:border-gray-400 hover:text-gray-900'
              }`}>
                Browse Courses
              </button>
            </div>
          </div>

          {/* Right 3D Quantum Visualization */}
          <div className="relative h-96 lg:h-[500px]">
            <div className="absolute inset-0 flex items-center justify-center">
              {/* 3D Cube */}
              <div 
                ref={cubeRef}
                className="relative"
                style={{ 
                  transformStyle: 'preserve-3d',
                  width: '120px',
                  height: '120px'
                }}
              >
                <div className="absolute bg-blue-500/30 border-2 border-blue-400" 
                     style={{
                       width: '120px',
                       height: '120px',
                       transform: 'rotateY(0deg) translateZ(60px)'
                     }} />
                <div className="absolute bg-purple-500/30 border-2 border-purple-400" 
                     style={{
                       width: '120px',
                       height: '120px',
                       transform: 'rotateY(90deg) translateZ(60px)'
                     }} />
                <div className="absolute bg-cyan-500/30 border-2 border-cyan-400" 
                     style={{
                       width: '120px',
                       height: '120px',
                       transform: 'rotateY(180deg) translateZ(60px)'
                     }} />
                <div className="absolute bg-pink-500/30 border-2 border-pink-400" 
                     style={{
                       width: '120px',
                       height: '120px',
                       transform: 'rotateY(-90deg) translateZ(60px)'
                     }} />
                <div className="absolute bg-green-500/30 border-2 border-green-400" 
                     style={{
                       width: '120px',
                       height: '120px',
                       transform: 'rotateX(90deg) translateZ(60px)'
                     }} />
                <div className="absolute bg-orange-500/30 border-2 border-orange-400" 
                     style={{
                       width: '120px',
                       height: '120px',
                       transform: 'rotateX(-90deg) translateZ(60px)'
                     }} />
              </div>

              {/* 3D Cone */}
              <div 
                ref={coneRef}
                className="absolute top-16 right-16"
                style={{ transformStyle: 'preserve-3d' }}
              >
                <div className="w-0 h-0 border-l-[40px] border-r-[40px] border-b-[80px] border-l-transparent border-r-transparent border-b-blue-400/60" />
              </div>

              {/* Floating particles */}
              <div className="absolute inset-0">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className={`absolute w-3 h-3 rounded-full ${
                      i % 4 === 0 ? 'bg-blue-400' :
                      i % 4 === 1 ? 'bg-purple-400' :
                      i % 4 === 2 ? 'bg-cyan-400' : 'bg-pink-400'
                    } opacity-60 animate-pulse`}
                    style={{
                      left: `${20 + (i * 60 / 8)}%`,
                      top: `${30 + Math.sin(i) * 40}%`,
                      animationDelay: `${i * 0.5}s`,
                      animationDuration: '2s'
                    }}
                  />
                ))}
              </div>

              {/* Orbital rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className={`absolute border-2 rounded-full opacity-20 ${
                      i === 0 ? 'border-blue-400' : i === 1 ? 'border-purple-400' : 'border-cyan-400'
                    }`}
                    style={{
                      width: `${200 + i * 60}px`,
                      height: `${200 + i * 60}px`,
                      animation: `spin ${8 + i * 2}s linear infinite`,
                      transform: `rotateX(${45 + i * 15}deg)`
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Foundations Section */}
        <section className="mb-20">
          <h2 className={`text-4xl font-light mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Foundations
          </h2>
          <p className={`text-lg mb-12 max-w-3xl ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Courses to learn about quantum information and how quantum computing works, from the basics onward.
          </p>
          
          <div className="grid gap-8 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            <CourseCard
              category="Quantum information and computation I"
              title="Basics of quantum information"
              description="Learn about quantum information, from states and measurements to quantum circuits and entanglement."
            />
            <CourseCard
              category="Quantum information and computation II"
              title="Fundamentals of quantum algorithms"
              description="Learn how quantum algorithms beat classical algorithms for problems including integer factoring and search."
            />
            <CourseCard
              category="Quantum information and computation III"
              title="General formulation of quantum information"
              description="Dive deeper into quantum information, including density matrices, channels, and general measurements."
            />
            <CourseCard
              category="Quantum information and computation IV"
              title="Foundations of quantum error correction"
              description="Learn how quantum computations can be protected against noise through quantum error correcting codes and fault tolerance."
              isNew={true}
            />
            <CourseCard
              category="Quantum computing in practice"
              title="Quantum computing in practice"
              description="Learn potential use cases and best practices for experimenting with quantum processors having 100+ qubits."
              isNew={true}
            />
          </div>
        </section>

        {/* Focused Topics Section */}
        <section className="mb-20">
          <h2 className={`text-4xl font-light mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Focused topics
          </h2>
          <p className={`text-lg mb-12 max-w-3xl ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Continue your learning journey by diving into more focused topics related to quantum computing.
          </p>
          
          <div className="grid gap-8 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            <CourseCard
              title="Quantum machine learning"
              description="Learn to leverage the power of quantum computing in machine learning methods."
              isNew={true}
            />
            <CourseCard
              title="Variational algorithm design"
              description="An overview of variational algorithms: hybrid classical quantum algorithms."
            />
            <CourseCard
              title="Quantum chemistry with VQE"
              description="An introduction to VQE that covers basic building blocks and applications."
            />
            <CourseCard
              title="Quantum diagonalization algorithms"
              description="Multiple quantum approaches to matrix diagonalization are explored, including VQE, QKD, SKD, and variations of these."
              isNew={true}
            />
            <CourseCard
              title="Utility-scale quantum computing"
              description="A collection of learning assets from a 14-lesson course on utility-scale quantum computing."
            />
          </div>
        </section>

        {/* Business Section */}
        <section className="mb-20">
          <h2 className={`text-4xl font-light mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Quantum computing for business
          </h2>
          <p className={`text-lg mb-12 max-w-3xl ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Explore the business applications of quantum computing and what it takes to build a successful quantum initiative.
          </p>
          
          <div className="grid gap-8 sm:grid-cols-1 lg:grid-cols-2">
            <CourseCard
              title="Quantum business foundations"
              description="Builds foundations of quantum computing, technology, and business applications."
            />
            <CourseCard
              title="Practical introduction to quantum-safe cryptography"
              description="Learn the risks that quantum computing poses to cryptography, and explore which approaches are quantum-safe."
            />
          </div>
        </section>

        {/* Classroom Section */}
        <section className="mb-20">
          <h2 className={`text-4xl font-light mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            GLOCYBS in classrooms
          </h2>
          <p className={`text-lg mb-12 max-w-3xl ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Explore a suite of instructional materials designed to incorporate quantum computing into traditional STEM courses.
          </p>
          
          <div className="grid gap-8 sm:grid-cols-1 lg:grid-cols-2">
            <CourseCard
              category="GLOCYBS in classrooms"
              title="Quantum mechanics"
              description="Use quantum computers to explore quantum phenomena like superposition and uncertainty."
              isNew={true}
              buttonText="Module"
            />
            <CourseCard
              category="GLOCYBS in classrooms"
              title="Computer science"
              description="Run some famous quantum algorithms, including Deutsch-Jozsa and QKD."
              isNew={true}
              buttonText="Module"
            />
          </div>
        </section>

        {/* Additional Resources */}
        <section className="mb-20">
          <h2 className={`text-4xl font-light mb-12 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Additional resources
          </h2>
          
          <div className="grid gap-12 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            <ResourceCard
              icon={Code2}
              title="Composer"
              description="Build and simulate circuits with a drag and drop interface"
              link="#"
            />
            <ResourceCard
              icon={Play}
              title="GLOCYBS YouTube"
              description="Browse lectures, tips and tricks, tutorials, community updates, and more"
              link="#"
            />
            <ResourceCard
              icon={Book}
              title="Tutorials"
              description="Explore utility-grade examples that leverage GLOCYBS and quantum technologies"
              link="#"
            />
          </div>
        </section>
      </main>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotateY(0deg); }
          to { transform: rotateY(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LearningPage;